import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TourProvider } from './context/TourContext';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import AuthenticatedLayout from './components/layout/AuthenticatedLayout';
import AdminRoute from './components/layout/AdminRoute';
import OnboardingTour from './components/OnboardingTour';
import { trackPageView } from './utils/analytics';
import { recordRecentVisit, labelForPath } from './components/CommandPalette';
import AnalyticsPage from './pages/AnalyticsPage';
// LT-487 / LT-489 retired the in-app LandingPage on the assumption that
// the unified marketing site at www.agentnoda.ai would own the `/`
// landing surface from cutover day. LT-496 (the cutover) is deferred,
// so app subdomains au.agentnoda.ai / in.agentnoda.ai still need an
// in-app landing surface — otherwise `/` falls through ProtectedRoute
// and redirects unauthenticated visitors straight to /login. Restored.
import LandingPage from './pages/LandingPage';
// LT-611: public, unauthenticated "Get called by our AI now" demo widget (GTM §7).
import DemoStandalonePage from './pages/LandingPage/DemoStandalonePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ResetPasswordConfirmPage from './pages/ResetPasswordConfirmPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import DashboardPage from './pages/DashboardPage';
import AgentsPage from './pages/AgentsPage';
import AgentDetailPage from './pages/AgentsPage/AgentDetailPage';
// LT-411: unified Agent create / edit form replaces the legacy
// /create-agent UAC page.
import AgentFormPage from './pages/AgentFormPage';
import RunCampaignPage from './pages/RunCampaignPage';
// LT-349 (LT-325 C2) — Campaign reintroduction. Replaces the legacy
// /campaigns → /sequences redirects with the new operator-facing pages
// over the LT-348 /campaigns API.
import CampaignsListPage from './pages/Campaigns/CampaignsListPage';
import CampaignCreateSplashPage from './pages/Campaigns/CampaignCreateSplashPage';
import CampaignUploadPage from './pages/Campaigns/CampaignUploadPage';
import CampaignReviewPage from './pages/Campaigns/CampaignReviewPage';
import CampaignFormPage from './pages/Campaigns/CampaignFormPage';
import CampaignWizardPage from './pages/Campaigns/CampaignWizardPage';
// Campaign cockpit revamp — replaces the legacy infinite-scroll
// CampaignDetailPage with a 3-column cockpit (left rail / tabbed center
// / right rail).
import CampaignCockpitPage from './pages/Campaigns/cockpit/CampaignCockpitPage';
// LT-532: Sequences UI retired — nav/routes/builder removed. The page
// components stay in the tree for drain compatibility but are no longer
// routed (bookmarked /sequences* links redirect to /campaigns).
import InboundCallsPage from './pages/InboundCallsPage';
import InboundConfigurationPage from './pages/InboundConfigurationPage';
import SettingsIntegrationsPage from './pages/SettingsIntegrationsPage';
import SettingsCrmPage from './pages/SettingsCrmPage';
import SettingsHubLayout, { RequireInbound } from './pages/SettingsHub/SettingsHubLayout';
import SettingsHubOverview from './pages/SettingsHub/SettingsHubOverview';
import SettingsAccountTab from './pages/SettingsHub/SettingsAccountTab';
import SettingsCallingTab from './pages/SettingsHub/SettingsCallingTab';
import SettingsTemplatesTab from './pages/SettingsHub/SettingsTemplatesTab';
import SettingsBillingTab from './pages/SettingsHub/SettingsBillingTab';
import SettingsSendersTab from './pages/SettingsHub/SettingsSendersTab';
import SettingsTeamTab from './pages/SettingsHub/SettingsTeamTab';
import EmailDeliverabilityTab from './pages/SettingsPage/EmailDeliverabilityTab/EmailDeliverabilityTab';
import PipelinesTab from './pages/SettingsPage/PipelinesTab/PipelinesTab';
import BookingPagesPage from './pages/BookingPagesPage';
import PublicBookingPage from './pages/PublicBookingPage';
import AdminPage from './pages/adminpages/AdminPage';
import AdminUserDetailPage from './pages/adminpages/AdminUserDetailPage';
import FeedbackPage from './pages/FeedbackPage';
import SupportPage from './pages/SupportPage';
import AdminFeedbacksPage from './pages/adminpages/AdminFeedbacksPage';
import AdminTicketsPage from './pages/adminpages/AdminTicketsPage';
import LeadsPage from './pages/LeadsPage';
import CustomVariablesPage from './pages/CustomVariablesPage';
import CallDetailPage from './pages/CallDetailPage';
import LeadDetailPage from './pages/LeadDetailPage';
import ContactsPage from './pages/ContactsPage';
import ContactDetailPage from './pages/ContactDetailPage';
import ContactListsPage from './pages/ContactListsPage';
import ContactListDetailPage from './pages/ContactListsPage/ContactListDetailPage';
import AccountsPage from './pages/AccountsPage';
import AccountDetailPage from './pages/AccountDetailPage';
import DealsPage from './pages/DealsPage';
import OpportunityDetailPage from './pages/OpportunityDetailPage';
import TranscriptViewerPage from './pages/TranscriptViewerPage';
import CallsPage from './pages/CallsPage';
import FollowUpsPage from './pages/FollowUpsPage/FollowUpsPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import DesignSystemPage from './pages/DesignSystemPage/DesignSystemPage';

function NavigateToRunCampaign({ tab }: { tab: string }) {
  const { runId } = useParams<{ runId: string }>();
  return <Navigate to={runId ? `/runs/${runId}?tab=${tab}` : '/dashboard'} replace />;
}

/** Fires a virtual page view on every route change. */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    // LT-312 — feed the Cmd-K palette's "Recent" group via localStorage.
    // Same trigger as the GA4 page view; zero extra listeners.
    recordRecentVisit(location.pathname, labelForPath(location.pathname));
  }, [location.pathname, location.search]);
  return null;
}

const AppContent: React.FC = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <TourProvider>
        <ScrollToTop />
        <RouteTracker />
        <div className="min-h-screen">
          <Routes>
          {/* Public routes. `/` renders the in-app landing surface until
              LT-496 cuts the marketing site over at www.agentnoda.ai —
              app subdomains au.agentnoda.ai / in.agentnoda.ai land here
              and unauthenticated visitors must not be punted to /login. */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          {/* LT-611: public live-demo widget — direct-linkable for ads / QR / outreach. */}
          <Route path="/demo" element={<DemoStandalonePage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          {/* LT-183: public booking page, unauthenticated */}
          <Route path="/book/:slug" element={<PublicBookingPage />} />
          
          {/* Protected routes with trailing * */}
          <Route path="/*" element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  {/* LT-349 (LT-325 C2) — Campaign reintroduction.
                      Replaces the LT-179 redirects to /sequences with the
                      new operator-facing initiative entity over the
                      LT-348 /campaigns API. */}
                  <Route path="/campaigns" element={<CampaignsListPage />} />
                  {/* LT-549 — two-path create splash. Static segment so it
                      ranks above the /campaigns/:id cockpit route. */}
                  <Route path="/campaigns/create" element={<CampaignCreateSplashPage />} />
                  {/* LT-551: Campaign-from-Brief upload + clarification UI. */}
                  <Route path="/campaigns/create/upload" element={<CampaignUploadPage />} />
                  {/* LT-552: upload-path review + Speak-to-Agent. */}
                  <Route path="/campaigns/upload/:campaignId/review" element={<CampaignReviewPage />} />
                  <Route path="/campaigns/new" element={<CampaignWizardPage />} />
                  <Route path="/campaigns/:id" element={<CampaignCockpitPage />} />
                  <Route path="/campaigns/:id/edit" element={<CampaignFormPage mode="edit" />} />
                  {/* LT-401 — wizard-based edit for drafts. The cockpit
                      Edit button routes drafts here; non-drafts still go
                      to the flat /edit form above. */}
                  <Route path="/campaigns/:id/edit-wizard" element={<CampaignWizardPage />} />
                  {/* LT-532: Sequences UI retired. The implicit per-workspace
                      voice sequence (LT-528) + Campaign retry policy (LT-529)
                      + on-demand follow-up dispatch (LT-530) replace the
                      builder surface. Bookmarked /sequences* links redirect
                      to /campaigns. Backend engine + page components remain
                      for drain compatibility (LT-533). */}
                  <Route path="/sequences/*" element={<Navigate to="/campaigns" replace />} />
                  <Route path="/new-run" element={<Navigate to="/campaigns/new" replace />} />
                  {/* LT-411: legacy /create-agent UAC page is retired —
                      bookmarks redirect to the unified agent catalogue. */}
                  <Route path="/create-agent" element={<Navigate to="/agents" replace />} />
                  {/* LT-341 — Agent catalogue UI (LT-324). */}
                  <Route path="/agents" element={<AgentsPage />} />
                  {/* LT-411 — unified Agent create / edit form. */}
                  <Route path="/agents/new" element={<AgentFormPage />} />
                  <Route path="/agents/:agentId/edit" element={<AgentFormPage />} />
                  {/* LT-383 — Agent detail page + capability editor. */}
                  <Route path="/agents/:agentId" element={<AgentDetailPage />} />
                  {/* LT-135: /leads → /contacts redirects. Existing
                      leads router handlers stay registered (AC R2 for
                      LT-133) but the sidebar now points users at
                      /contacts. These redirects keep bookmarked /leads
                      URLs working. The /leads/:leadId detail page is
                      still live for now — LT-136's read-flip will fold
                      it into /contacts/:contactId. */}
                  <Route path="/leads" element={<Navigate to="/contacts" replace />} />
                  {/* LT-311: /custom-fields moves under the Settings hub.
                      Direct-link redirect keeps older bookmarks alive. */}
                  <Route path="/custom-fields" element={<Navigate to="/settings/custom-fields" replace />} />
                  <Route path="/custom-variables" element={<Navigate to="/settings/custom-fields" replace />} />
                  {/* Kept for deep links from existing tooling until LT-136 */}
                  <Route path="/leads-legacy" element={<LeadsPage />} />
                  <Route path="/leads/:leadId" element={<LeadDetailPage />} />
                  {/* LT-134: Contacts + Activity timeline */}
                  <Route path="/contacts" element={<ContactsPage />} />
                  <Route path="/contacts/:contactId" element={<ContactDetailPage />} />
                  {/* LT-359: Contact Lists (Phase 3 Epic 6) */}
                  <Route path="/contact-lists" element={<ContactListsPage />} />
                  <Route path="/contact-lists/:listId" element={<ContactListDetailPage />} />
                  {/* LT-135: Accounts */}
                  <Route path="/accounts" element={<AccountsPage />} />
                  <Route path="/accounts/:accountId" element={<AccountDetailPage />} />

                  {/* LT-140 — Deals / Opportunities */}
                  <Route path="/deals" element={<DealsPage />} />
                  <Route path="/deals/:opportunityId" element={<OpportunityDetailPage />} />
                  <Route path="/follow-ups" element={<FollowUpsPage />} />
                  {/* LT-311: templates move under the Settings hub.
                      `?type=sms` flag preserves the deep-link target. */}
                  <Route path="/sms-templates" element={<Navigate to="/settings/templates?type=sms" replace />} />
                  <Route path="/email-templates" element={<Navigate to="/settings/templates" replace />} />
                  <Route path="/runs/:runId" element={<RunCampaignPage />} />
                  <Route path="/runs/:runId/calls/:callSummaryId" element={<CallDetailPage />} />
                  <Route path="/calls/:callSummaryId/transcript" element={<TranscriptViewerPage />} />
                  {/* Call Details page (intelligence panel) — primary drill-in
                      from the Calls list and the Contact timeline. The
                      transcript is a separate page linked from here. */}
                  <Route path="/calls/:callSummaryId" element={<CallDetailPage />} />
                  {/* LT-255: top-level Calls list over GET /calls/ (DB-backed per LT-253). */}
                  <Route path="/calls" element={<CallsPage />} />
                  <Route path="/runs/:runId/progress" element={<NavigateToRunCampaign tab="progress" />} />
                  <Route path="/analytics" element={<AnalyticsPage />} />
                  {/* LT-311: subscription + credits move under
                      Settings → Billing. Redirects keep external links alive. */}
                  <Route path="/subscription" element={<Navigate to="/settings/billing" replace />} />
                  <Route path="/credits" element={<Navigate to="/settings/billing?tab=credits" replace />} />
                  {/* /inbound-calls renders the list directly. The page shows an
                      empty state when no inbound calls exist; we don't redirect
                      plan-gated users to Billing — that confused users
                      navigating from the sidebar. Write-side gating still lives
                      on Settings → Inbound config. */}
                  <Route path="/inbound-calls" element={<InboundCallsPage />} />
                  {/* LT-311: /inbound-configuration relocated under the hub. */}
                  <Route path="/inbound-configuration" element={<Navigate to="/settings/inbound-config" replace />} />
                  {/* Sidebar's Profile link points at /profile; the actual
                      profile surface lives under Settings → Account. Without
                      this redirect the catch-all sends users to /dashboard
                      and Profile silently equals Dashboard. */}
                  <Route path="/profile" element={<Navigate to="/settings/account" replace />} />
                  {/* LT-311 — Settings hub. Nested sub-routes drive tabs;
                      each sub-route is independently addressable so deep
                      links work (AC 2) and guard-level entitlement /
                      admin checks happen per-sub-route. */}
                  <Route path="/settings" element={<SettingsHubLayout />}>
                    <Route index element={<SettingsHubOverview />} />
                    <Route path="account" element={<SettingsAccountTab />} />
                    {/* LT-379: per-user default Agent picker. */}
                    <Route path="calling" element={<SettingsCallingTab />} />
                    <Route path="team" element={<SettingsTeamTab />} />
                    <Route path="senders" element={<SettingsSendersTab />} />
                    <Route path="email-deliverability" element={<EmailDeliverabilityTab />} />
                    <Route path="pipelines" element={<PipelinesTab />} />
                    <Route path="templates" element={<SettingsTemplatesTab />} />
                    <Route path="custom-fields" element={<CustomVariablesPage />} />
                    <Route path="booking-pages" element={<BookingPagesPage />} />
                    <Route path="calendars" element={<SettingsIntegrationsPage />} />
                    <Route path="integrations" element={<SettingsIntegrationsPage />} />
                    {/* LT-187: CRM integration subpage nested under the hub. */}
                    <Route path="integrations/crm" element={<SettingsCrmPage />} />
                    <Route
                      path="inbound-config"
                      element={
                        <RequireInbound>
                          <InboundConfigurationPage />
                        </RequireInbound>
                      }
                    />
                    <Route path="billing" element={<SettingsBillingTab />} />
                  </Route>
                  {/* LT-311: /booking-pages relocated under the hub. */}
                  <Route path="/booking-pages" element={<Navigate to="/settings/booking-pages" replace />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  {/* LT-308 · DS v1 showcase. Every component × every variant. */}
                  <Route path="/dev/design-system" element={<DesignSystemPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  {/* Legacy redirects for backward compatibility */}
                  <Route path="/support-feedback" element={<Navigate to="/feedback" replace />} />
                  <Route path="/raise-tickets" element={<Navigate to="/support" replace />} />
                  <Route path="/admin" element={
                    <AdminRoute>
                      <AdminPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/:userId" element={
                    <AdminRoute>
                      <AdminUserDetailPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/global-settings" element={<Navigate to="/admin?tab=global-settings" replace />} />
                  <Route path="/admin/feedbacks" element={
                    <AdminRoute>
                      <AdminFeedbacksPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/tickets" element={
                    <AdminRoute>
                      <AdminTicketsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/pricing-settings" element={<Navigate to="/admin?tab=pricing-settings" replace />} />
                  <Route path="/admin/privacy-terms" element={<Navigate to="/admin?tab=privacy-terms" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AuthenticatedLayout>
            </ProtectedRoute>
          } />

          {/* Special case for password reset confirm which is public but might be accessed while logged in */}
          <Route path="/reset-password-confirm" element={
            <AuthenticatedLayout>
              <ResetPasswordConfirmPage />
            </AuthenticatedLayout>
          } />

          {/* Catch-all for unknown public routes — falls through to the
              protected `/*` block above which redirects to /login when
              unauthenticated and /dashboard when authenticated. */}
        </Routes>
        </div>
        <OnboardingTour />
      </TourProvider>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
